import moment from 'moment';
import { v4, v5 } from 'uuid';
import { VisitTool } from '../api/fetchAllVisitsForPatient';
import { Px } from '../api/fetchPrescriptionById';
import { GenieMemoryItem, GeniePadElementsSettingItem } from '../api/pushGenie';

import { PrintConfig } from '../api/pushVisit';
import { getStore } from '../App';
import { calculateQuantity } from '../components/prescriptionPad/rightPanel/workspace/padV2/medUtils';
import {
    DEFAULT_MEDICATIONS_TABLE_CONFIG,
    DEFAULT_PAD_CONFIG,
    DEFAULT_PRINT_CONFIG,
    FIXED_QUANTITIES,
    FLAVOUR,
    FOLLOW_UP_ADVICES_PAD_CONFIG,
    LOCAL_ID_PREFIX,
    MED_TABLE_COL_QUANTITY_KEY,
    PAD_CONFIG_FOLLOW_UP_ADVICES_ID,
} from '../constants';
import { GDyForm } from './db';
import { getGenieSetting } from './selectors/session';
import { Age, Patient } from './types/patient';
import {
    Duration,
    InjectionsEntity,
    LabTestsEntityOrProceduresEntity,
    LabVitalEntity,
    MedDoseInfo,
    MedicationsEntity,
    Prescription,
    Tool,
} from './types/prescription';
import {
    LAB_TESTS_FOLLOWUP_DATE_TYPE,
    LAB_TESTS_FOLLOWUP_TIME_TYPE,
} from '../components/common/optionGeneratorUtils';
import { labTestFollowUpUnitType } from '../components/prescriptionPad/rightPanel/workspace/padV2/LabTestsDateSelector';
import { DocProfileResponse } from '../api/fetchDoctorProfile';
import { FORM_TO_DF_MAPPING } from '../components/prescriptionPad/rightPanel/workspace/mxProperties';
import { CurrentMedications } from './types/medicalHistory';

export const randomString = (length: number): string => {
    const CHARS = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let result = '';
    for (let i = length; i > 0; --i) result += CHARS[Math.floor(Math.random() * CHARS.length)];
    return result;
};

// returns age in years (age) and months (month) and days (day)
export const calculateAge = (
    dob: string,
    visitDate?: string,
): { age: string; month: string; day: string } => {
    const today = visitDate ? moment(new Date(visitDate)) : moment(new Date());
    const dobInMoment = moment(dob);
    const years = today.diff(dobInMoment, 'year');
    dobInMoment.add(years, 'years');
    const months = today.diff(dobInMoment, 'months');
    dobInMoment.add(months, 'months');
    const days = today.diff(dobInMoment, 'days');

    return { age: years.toString(), month: months.toString(), day: days.toString() };
};

export const toTitleCase = (str: string): string => {
    return str.replace(/\w\S*/g, function (txt) {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
};

const SINGULARS: { [k: string]: string } = {
    hour: 'Hour',
    hours: 'Hour',
    days: 'Day',
    day: 'Day',
    weeks: 'Week',
    week: 'Week',
    months: 'Month',
    month: 'Month',
    years: 'Year',
    year: 'Year',
    yr: 'yr',
    yrs: 'yr',
    mth: 'mth',
    mths: 'mth',
    min: 'min',
    mins: 'min',
    exception: 'Exception',
    exceptions: 'Exception',
    doctor: 'Doctor',
    doctors: 'Doctor',
    drug: 'drug',
    drugs: 'drugs',
    time: 'time',
    times: 'time',
    records: 'record',
};

const PURALS: { [k: string]: string } = {
    hour: 'Hours',
    hours: 'Hours',
    days: 'Days',
    day: 'Days',
    weeks: 'Weeks',
    week: 'Weeks',
    months: 'Months',
    month: 'Months',
    years: 'Years',
    year: 'Years',
    yr: 'yrs',
    yrs: 'yrs',
    mth: 'mths',
    mths: 'mths',
    min: 'mins',
    mins: 'mins',
    exception: 'Exceptions',
    exceptions: 'Exceptions',
    doctor: 'Doctors',
    doctors: 'Doctors',
    drug: 'drugs',
    drugs: 'drugs',
    time: 'times',
    times: 'times',
    records: 'records',
};

export const getUnit = (selected: string, value: string): string => {
    const qt = parseInt(value);
    if (isNaN(qt)) {
        return selected;
    }
    const ce =
        qt === 1
            ? SINGULARS[selected.toLowerCase()] || selected
            : PURALS[selected.toLowerCase()] || selected;
    return ce;
};

export const getDigitSuffix = (digit: number): string => {
    return digit === 1 ? 'st' : digit === 2 ? 'nd' : 'th';
};

const parseAge = (
    a: {
        age?: string | null;
        month?: string | null;
        dob?: string | null;
        ageInYear?: string | null;
        ageInMonths?: string | null;
    },
    visitDate?: string,
) => {
    let aa, am, ad;

    if (a.dob) {
        const temp = calculateAge(a.dob, visitDate);
        aa = temp.age;
        am = temp.month;
        ad = temp.day;
    } else {
        aa = a.age || a.ageInYear;
        am = a.month || a.ageInMonths;
    }

    const ageInt = parseInt(aa || '0');
    const ageMonthInt = parseInt(am || '0');
    const ageDayInt = parseInt(ad || '0');

    return { aa, am, ad, ageInt, ageMonthInt, ageDayInt };
};

export const getAgeString = (
    a: { age?: string; month?: string; dob?: string },
    prefix?: string,
    suffix?: string,
    visitDate?: string,
): string => {
    const { aa, am, ad, ageInt, ageMonthInt, ageDayInt } = parseAge(a, visitDate);

    if (isNaN(ageInt) && isNaN(ageMonthInt)) {
        return '';
    }

    if (aa && am && ageInt > 0 && ageMonthInt > 0) {
        return `${prefix || ''}${ageInt}${'y'}, ${ageMonthInt}${'m'}${suffix || ''}`;
    } else if (aa && ageInt > 0) {
        return `${prefix || ''}${ageInt}${'y'}${suffix || ''}`;
    } else if (am && ageMonthInt > 0) {
        if (ageMonthInt > 11) {
            return getAgeString(
                { age: `${ageMonthInt / 12}`, month: `${ageMonthInt % 12}` },
                prefix,
                suffix,
            );
        }
        return `${prefix || ''}${ageMonthInt}${'m'} ${ad && ageDayInt > 0 ? `, ${ad}${'d'}` : ''}${
            suffix || ''
        }`;
    } else if (ad && ageDayInt > 0) {
        return `${prefix || ''}${ad}${'d'}${suffix || ''}`;
    }

    return '';
};

export const LOCAL_PT_ID_PREFIX = `P-`;
export const generatePatientId = (ph: string, name: string): string =>
    `${LOCAL_PT_ID_PREFIX}${ph}-${name
        .toLowerCase()
        .replace(/\s/g, '')
        .replace(/[^a-z\d\s]+/gi, '')}`;

export const generateVisitId = (docId: string): string => `d-${v4()}`;

const MY_NAMESPACE = '0a038a2e-23e0-467f-9251-71a62e661b89';
export const generatePrescriptionId = (docId: string, visitId: string): string => {
    // 1 is number of prescription of that visit id
    const hash = v5(`1-${docId}-${visitId}`, MY_NAMESPACE);
    return `P-${FLAVOUR}-${hash}`.toUpperCase();
};

export const generateSpecialityId = (speciality: string): string => {
    const hash = v5(speciality, MY_NAMESPACE);
    return hash;
};

export const sideScroll = (element: Element, distance: number): void => {
    if (element?.scrollBy) {
        element.scrollBy({
            left: distance,
            behavior: 'smooth',
        });
    }
};

export const getAgeComponents = (duration: moment.Duration) => {
    const year = duration.years();
    const months = duration.months();
    const days = duration.days();

    const ageStringArr = [];
    let ageObj;

    if (year) {
        const yearString = year.toString();
        ageStringArr.push(`${yearString}Y`);
        const unit = getUnit('years', yearString);
        ageObj = { value: yearString, unit };
    }

    if (months) {
        const monthString = months.toString();
        ageStringArr.push(`${monthString}M`);
        const unit = getUnit('months', monthString);
        if (!ageObj) ageObj = { value: monthString, unit };
    }

    if (days) {
        const dayString = months.toString();
        ageStringArr.push(`${dayString}D`);
        const unit = getUnit('days', dayString);
        if (!ageObj) ageObj = { value: dayString, unit };
    }

    const ageString = ageStringArr.join(' ');

    return { year, months, days, ageString, ageObj };
};

export function hashCode(str: string): number {
    let hash = 0,
        i = 0;
    const len = str.length;
    while (i < len) {
        hash = ((hash << 5) - hash + str.charCodeAt(i++)) << 0;
    }
    return hash + 2147483647 + 1;
}

export const generateElementId = (name: string): string => {
    const hash = hashCode(name.substring(0, 200).toLowerCase());
    return `${LOCAL_ID_PREFIX}${hash}`;
};

export const generateDySecId = (): string => {
    const hash = v4();
    return hash;
};

export const generateLocalId = (): string =>
    `${LOCAL_ID_PREFIX}${new Date().getTime()}-${randomString(4)}`;

export const generateLocalIdForEntity = (id: string): string => {
    return `${LOCAL_ID_PREFIX}${id}-${new Date().getTime()}-${randomString(4)}`;
};

export const generateRandomNumber = (min: number, max: number): number =>
    min + Math.floor(Math.random() * max);

export interface Item {
    id: string;
}

export const cloneAndUpsertArray = <I extends Item>(items: I[], array?: I[]): I[] => {
    let clone = array ? [...array] : [];
    for (let i = 0; i < items.length; i++) {
        clone = cloneAndUpsert(items[i], clone);
    }

    return clone;
};

export const cloneAndUpsert = <I extends Item>(item: I, array?: I[], index?: number): I[] => {
    const clone = array ? [...array] : [];

    if (index !== undefined && index < clone.length) {
        clone[index] = item;
        return clone;
    }

    const i = clone.findIndex((_item) => _item.id === item.id);
    if (i > -1) clone[i] = { ...item };
    else clone.push(item);

    return clone;
};

export function mergeVitals(
    selectedVital: LabVitalEntity[],
    pxVitals?: LabVitalEntity[],
): LabVitalEntity[] {
    if (!pxVitals) {
        return selectedVital;
    }

    return cloneAndUpsertArray(selectedVital, pxVitals);
}

export function toBookAppointmentPatient(
    p: Patient,
): {
    ln: string;
    mobile?: string;
    dob: string;
    fn: string;
    uuid: string;
    oid: string;
    gen: string;
} {
    return {
        ln: p.personal.name.l ?? '',
        mobile: p.personal.phone?.n,
        dob: p.personal.age?.dob ?? '',
        fn: p.personal.name.f,
        uuid: (p.formData?.find((f) => f.key === 'uhid')?.value as string) ?? '',
        oid: p.localId,
        gen: p.personal.gender,
    };
}

export const cloneAndDelete = <I extends Item>(itemId: string, array?: I[]): I[] => {
    if (!array) return [];

    const clone = [...array];
    return clone.filter((_item) => _item.id !== itemId);
};

export const getAgeInMonths = (a: Age): number | undefined => {
    const ageInt = parseInt(a.value || '0');
    const ageMonthInt = parseInt(a.month || '0');

    if (isNaN(ageInt) || isNaN(ageMonthInt)) {
        return;
    }

    if (a.value && a.month && ageInt > 0 && ageMonthInt > 0) {
        return ageInt * 12 + ageMonthInt;
    } else if (a.value && ageInt > 0) {
        return ageInt * 12;
    } else if (a.month && ageMonthInt > 0) {
        return ageMonthInt;
    }

    return;
};

export const toShortDateString = (d: Date): string => {
    return `${d.toLocaleString('default', {
        month: 'short',
    })} '${d.toLocaleString('default', {
        year: '2-digit',
    })}`;
};

export const sanitizeSuggestion = (name: string): string => {
    const regex = /[0-9!@#$%^&*()_+{}\[\]:;<>,.?~\\/-]/g;
    return name.replace(regex, '');
};

export function splitFullName(
    name: string,
): {
    firstName: string;
    middleName: string;
    lastName: string;
} {
    const nameArr = name.trim().split(' ');

    if (nameArr.length === 1) {
        return {
            firstName: nameArr[0],
            middleName: '',
            lastName: '',
        };
    }

    return {
        firstName: nameArr[0],
        middleName: nameArr.slice(1, nameArr.length - 1).join(' '),
        lastName: nameArr[nameArr.length - 1],
    };
}

export const getAbhaSuggestions = (name: string, mobile: string): string => {
    console.log({ name });
    const sanitised_name = sanitizeSuggestion(name.replaceAll(' ', ''));
    const firstPart = sanitised_name.length >= 5 ? sanitised_name.substring(0, 5) : sanitised_name;

    // Extract the last 5 characters of the last string
    const lastPart = mobile.slice(-5);

    // Generate a random digit between 0 and 9
    const randomDigit = Math.floor(Math.random() * 100);

    // Concatenate the parts and return
    return firstPart.trim().toLowerCase() + randomDigit + lastPart.toLowerCase();
};

export const smoothScrollToItemWithDelay = (id: string, delay: number = 200): void => {
    setTimeout(() => {
        const myContainer = document.getElementById(id) as HTMLDivElement;
        if (myContainer) {
            myContainer.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest',
            });
        }
    }, delay);
};

export const isDoctorNOSA = (doc: DocProfileResponse | undefined): boolean => {
    const categories = doc?.profile.professional.category || [];
    return categories.includes('NOSA');
};

export const isDoctorInternal = (doc: DocProfileResponse | undefined): boolean => {
    const categories = doc?.profile.professional.category || [];
    return categories.includes('IN');
};

export const generateVisitName = (visitCreatedAt: string, pres?: Prescription): string => {
    if (!pres) {
        return `Visit on ${moment(visitCreatedAt).format('Do MMMM')}`;
    }

    return (
        pres.diagnosis?.map((d) => d.name)?.join(', ') ||
        pres.symptoms?.map((d) => d.name)?.join(', ') ||
        pres.labTests?.map((d) => d.name)?.join(', ') ||
        `Visit on ${moment(pres?.date).format('Do MMMM')}`
    );
};

export const migrateToNew = (printConfig: PrintConfig): PrintConfig => {
    const t: PrintConfig = { dyform: {} };

    Object.entries({ ...DEFAULT_PRINT_CONFIG, ...printConfig }).forEach(([k, v]) => {
        if (typeof v === typeof DEFAULT_PRINT_CONFIG?.[k]) {
            if (
                typeof DEFAULT_PRINT_CONFIG[k] === 'object' &&
                typeof v === 'object' &&
                Object.keys(DEFAULT_PRINT_CONFIG[k])?.length !== Object.keys(v)?.length
            ) {
                t[k] = {
                    ...(DEFAULT_PRINT_CONFIG[k] as { [key: string]: boolean }),
                    ...v,
                };
            } else {
                t[k] = v;
            }
        } else {
            if (k === 'patientMedicalConditions') {
                t['patientMedicalHistory'] = {
                    ...((DEFAULT_PRINT_CONFIG.patientMedicalHistory || {}) as {
                        [k: string]: boolean;
                    }),
                    ...((printConfig?.patientMedicalHistory || {}) as {
                        [k: string]: boolean;
                    }),
                    patientMedicalConditions: v as boolean,
                };
            } else if (typeof DEFAULT_PRINT_CONFIG[k] === 'object' && typeof v === 'boolean') {
                const defualtValues: { [k: string]: boolean } = {};

                Object.assign(defualtValues, DEFAULT_PRINT_CONFIG[k]);

                t[k] = {
                    ...defualtValues,
                    entity: v,
                };
            } else {
                t[k] = v;
            }
        }
    });

    return t;
};

export const uniqueBy = <T>(uniqueKey: keyof T, objects: T[]): T[] => {
    const ids = objects.map((object) => object[uniqueKey]);
    return objects.filter((object, index) => !ids.includes(object[uniqueKey], index + 1));
};

export function detectBrowser(): 'Chrome' | 'Safari' | 'Firefox' | 'Unknown' {
    if (navigator.userAgent.indexOf('Chrome') != -1) {
        return 'Chrome';
    } else if (navigator.userAgent.indexOf('Safari') != -1) {
        return 'Safari';
    } else if (navigator.userAgent.indexOf('Firefox') != -1) {
        return 'Firefox';
    } else {
        return 'Unknown';
    }
}

export const getPadElementMergedWithDefault = (
    padConfig: GeniePadElementsSettingItem[],
    gDyForms: GDyForm[],
): GeniePadElementsSettingItem[] => {
    const remainaingDefaultPadElements =
        DEFAULT_PAD_CONFIG?.filter((ele) => {
            return padConfig?.find((defele) => defele.id === ele.id) ? false : true;
        }) || [];

    let elementPadConfig: GeniePadElementsSettingItem[] = [];

    // TODO remove this logic after a month
    if (
        padConfig.length &&
        remainaingDefaultPadElements.find((t) => t.id === PAD_CONFIG_FOLLOW_UP_ADVICES_ID)
    ) {
        elementPadConfig = [
            padConfig[0],
            padConfig[1],
            FOLLOW_UP_ADVICES_PAD_CONFIG,
            ...padConfig.slice(2),
            ...remainaingDefaultPadElements.filter((t) => t.id !== PAD_CONFIG_FOLLOW_UP_ADVICES_ID),
        ].filter(Boolean);
    } else {
        elementPadConfig = [...padConfig, ...remainaingDefaultPadElements];
    }

    const allSections = gDyForms.map((item) => ({
        id: item.id,
        isShown: true,
        name: item.name,
    }));

    const remainingDyFormSections = allSections.filter((s) =>
        elementPadConfig.find((item) => item.id === s.id) ? false : true,
    );

    return [...elementPadConfig, ...remainingDyFormSections];
};

export const getMedElementsMergedWithDefault = (
    medConfig: GeniePadElementsSettingItem[],
): GeniePadElementsSettingItem[] => {
    const set = new Set(medConfig.map((item) => item.id));
    const remainaingDefaultMedElements =
        DEFAULT_MEDICATIONS_TABLE_CONFIG?.filter((ele) => {
            return !set.has(ele.id);
        }) || [];

    return [...medConfig, ...remainaingDefaultMedElements];
};

export const createDBTool = (px: Px | Prescription | VisitTool): Tool => {
    const t = 'tool' in px ? px.tool : px;
    return {
        examinations:
            'medicalHistory' in t
                ? t.medicalHistory?.examinations
                : 'examinations' in t
                ? t.examinations
                : undefined,
        diagnosis: t.diagnosis,
        symptoms: t.symptoms,
        vitals:
            'medicalHistory' in t ? t.medicalHistory?.vitals : 'vitals' in t ? t.vitals : undefined,
        medications: t.medications,
        labTests: t.labTests,
        procedures: t.procedures,
        followup: t.followup,
        advices: t.advices,
        prescriptionNotes: t.prescriptionNotes,
        privateNotes: t.privateNotes,
        dyform: t.dyform,
        refer: t.refer,
        labRefer: t.labRefer,
        labVitals: t.labVitals,
        dentalExaminations: t.dentalExaminations,
        dentalProcedures: t.dentalProcedures,
        opVision: t.opVision,
        opIop: t.opIop,
        opCurrentSpec: t.opCurrentSpec,
        opSubjectiveRefraction: t.opSubjectiveRefraction,
        opAutoRefraction: t.opAutoRefraction,
        opFinalPrescription: t.opFinalPrescription,
        injections: t.injections,
        careCanvas: t.careCanvas,
    };
};

export const filterIdUsingStartsWithAndJoin = <
    T extends {
        id: string;
    }
>(
    itemArr: T[],
    searchString: string,
): string => {
    return itemArr
        .filter((i) => !i.id.startsWith(searchString))
        .map((item) => item.id)
        .join(',');
};

export const medWithQuantity = (
    element: MedicationsEntity,
    isEnabled?: boolean,
    avoidRecalculateQt?: boolean,
): MedicationsEntity => {
    if (avoidRecalculateQt) return element;
    let qtColumnEnabled = isEnabled;
    if (qtColumnEnabled === undefined) {
        const store = getStore();
        const medElements = getGenieSetting(
            store.getState(),
            'medElements',
        ) as GeniePadElementsSettingItem[];
        qtColumnEnabled = medElements.find((ele) => ele.id === MED_TABLE_COL_QUANTITY_KEY)?.isShown;
    }

    const quantity =
        FIXED_QUANTITIES?.[
            (element?.df_id as keyof typeof FIXED_QUANTITIES) ||
                FORM_TO_DF_MAPPING?.[element.form || element.dosage_form || '']
        ];

    if (quantity) {
        return {
            ...element,
            quantity: {
                custom: qtColumnEnabled ? quantity : undefined,
            },
            tapering_dose: element.tapering_dose?.map((dose) => ({
                ...dose,
                quantity: {
                    custom: qtColumnEnabled ? quantity : undefined,
                },
            })),
        };
    }

    return {
        ...element,
        quantity: {
            custom: qtColumnEnabled
                ? calculateQuantity(element.frequency, element.duration, element.dose)
                : undefined,
        },
        tapering_dose: element.tapering_dose?.map((dose) => ({
            ...dose,
            quantity: {
                custom: qtColumnEnabled
                    ? calculateQuantity(dose.frequency, dose.duration, dose.dose)
                    : undefined,
            },
        })),
    };
};

export const sanitizeMed = (
    element: MedicationsEntity,
    medElements?: GeniePadElementsSettingItem[],
    medRouteEnabled?: boolean,
): MedicationsEntity => {
    let medElements2 = medElements;
    let medRouteEnabled2 = medRouteEnabled;
    if (medElements2 === undefined) {
        const store = getStore();
        medElements2 = getGenieSetting(
            store.getState(),
            'medElements',
        ) as GeniePadElementsSettingItem[];
    }

    if (medRouteEnabled2 === undefined) {
        const store = getStore();
        medRouteEnabled2 = getGenieSetting(
            store.getState(),
            'medRouteEnabled',
        ) as boolean;
    }

    medElements2.forEach((t) => {
        if (!medRouteEnabled2) {
            delete element['route'];
            if (!element.soa) {
                delete element['area'];
            }
            element.tapering_dose?.map((tempering_dose) => {
                delete tempering_dose['route'];
                if (!element.soa) {
                    delete element['area'];
                }
            });
        }

        if (!t.isShown) {
            if (t.id === 'start_from') {
                delete element['to_day'];
            }

            if (t.id === 'dose') {
                delete element['area'];
                delete element['route'];
            }

            delete element[t.id as keyof MedicationsEntity];

            element.tapering_dose?.map((tempering_dose) => {
                if (t.id === 'start_from') {
                    delete tempering_dose['to_day'];
                }

                if (t.id === 'dose') {
                    delete tempering_dose['area'];
                    delete tempering_dose['route'];
                }

                delete tempering_dose[t.id as keyof MedDoseInfo];
            });
        }
    });
    delete element.hxng_status;
    return element;
};

export interface AgeInYears {
    age?: string | null;
    month?: string | null;
    dob?: string | null;
    ageInYear?: string | null;
    ageInMonths?: string | null;
}

export const getAgeInYears = (a: AgeInYears): number => {
    const { ageInt, ageMonthInt, ageDayInt } = parseAge(a);
    return Math.ceil(ageInt + ageMonthInt / 12 + ageDayInt / 365);
};

export const getAgeInYearsWithoutRoundOff = (a?: AgeInYears, visitDate?: string): number => {
    if (!a) {
        return 0;
    }
    const { ageInt, ageMonthInt, ageDayInt } = parseAge(a, visitDate);
    return ageInt + ageMonthInt / 12 + ageDayInt / 365;
};

export const getAgeInMonthsWithoutRoundOff = (a?: AgeInYears, visitDate?: string): number => {
    if (!a) {
        return 0;
    }
    const { ageInt, ageMonthInt } = parseAge(a, visitDate);
    return ageInt * 12 + ageMonthInt;
};

export function generateLabTestsFollowUpOptions(
    units: {
        label: string;
        prefix?: string;
        type: LAB_TESTS_FOLLOWUP_DATE_TYPE;
        timeType: LAB_TESTS_FOLLOWUP_TIME_TYPE;
    }[],
    newValue: string,
): labTestFollowUpUnitType[] {
    const t = newValue?.trim();

    if (!t) {
        return [];
    }

    const n = t.split(' ');

    const pi = parseInt(n[0]);

    if (isNaN(pi)) {
        return [];
    }

    return units.map((c) => ({
        label: `${c?.prefix ? `${c.prefix} ` : ''}${pi} ${getUnit(c.label, pi.toString())}`,
        value: pi.toString(),
        type: c.type,
        timeType: c.timeType,
    }));
}

export function getTimeSince(dateString: string): { qt: number; unit: string } {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (days > 0) {
        return { qt: days, unit: 'Day' };
    }
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    return { qt: hours, unit: 'Hour' };
}

export function convertToCurrentMedications(
    med: MedicationsEntity | InjectionsEntity,
    visitDate: string,
): CurrentMedications {
    const timeSince = getTimeSince(visitDate);

    let notes = undefined;
    if ('instruction' in med && med.instruction !== undefined) {
        notes = med.instruction;
    } else if ('instructions' in med && med.instructions !== undefined) {
        notes = med.instructions;
    }

    return {
        id: med.id,
        name: med.name,
        frequency: { custom: med.frequency?.custom || '' },
        since: timeSince ? { custom: `${timeSince.qt} ${timeSince.unit}` } : undefined,
        notes,
        reportedAt: new Date().toISOString(),
        status: 'Active',
    };
}

export function filterSuggestion(
    config: GenieMemoryItem[] | undefined,
    selectedOption: MedicationsEntity | LabTestsEntityOrProceduresEntity | InjectionsEntity,
): MedicationsEntity | LabTestsEntityOrProceduresEntity | InjectionsEntity {
    if (!config) return selectedOption;
    const fieldsToHide = config.filter((field) => !field.remember);
    const clone = {
        ...selectedOption,
    };
    fieldsToHide.forEach((field) => {
        const key = (field?.id || '').toLowerCase();
        // (clone as any)[key] = undefined;
        delete (clone as any)[key];
    });
    return clone;
}
